import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  DollarSign, TrendingUp, TrendingDown, CreditCard, AlertTriangle,
  RefreshCw, FileText, Clock, CheckCircle, XCircle,
  Users, Calendar, ChevronLeft, ChevronRight, RotateCcw, AlertCircle
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';

const FinancialOperations = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [financialData, setFinancialData] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [pastDueAccounts, setPastDueAccounts] = useState([]);
  const [cancelledAccounts, setCancelledAccounts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [retrying, setRetrying] = useState(null);
  const limit = 20;

  useEffect(() => {
    fetchFinancialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'subscriptions') {
      fetchSubscriptions();
    } else if (activeTab === 'past-due') {
      fetchPastDueAccounts();
    } else if (activeTab === 'cancelled') {
      fetchCancelledAccounts();
    } else if (activeTab === 'invoices') {
      fetchInvoices();
    } else if (activeTab === 'webhooks') {
      fetchWebhookLogs();
    }
  }, [activeTab, page]);

  const fetchFinancialData = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getFinancialOverview();
      setFinancialData(res.data);
    } catch (error) {
      console.error('Failed to fetch financial data:', error);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const res = await adminAPI.getUsers({ account_type: 'employer', skip: page * limit, limit });
      const subs = res.data.users.map(u => ({
        id: u.id,
        email: u.email,
        company: u.profile?.company_name || 'N/A',
        tier: u.profile?.subscription_tier || 'free',
        status: u.profile?.billing_status || (u.profile?.trial_active ? 'trial' : 'none'),
        stripeCustomerId: u.profile?.stripe_customer_id,
        startDate: u.profile?.subscription_updated_at || u.created_at,
        trialEnd: u.profile?.trial_end_date
      }));
      setSubscriptions(subs);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
    }
  };

  const fetchPastDueAccounts = async () => {
    try {
      const res = await adminAPI.getPastDueAccounts({ skip: page * limit, limit });
      setPastDueAccounts(res.data.accounts || []);
    } catch (error) {
      console.error('Failed to fetch past-due accounts:', error);
    }
  };

  const fetchCancelledAccounts = async () => {
    try {
      const res = await adminAPI.getCancelledAccounts({ skip: page * limit, limit });
      setCancelledAccounts(res.data.cancellations || []);
    } catch (error) {
      console.error('Failed to fetch cancelled accounts:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      const res = await adminAPI.getInvoiceHistory({ skip: page * limit, limit });
      setInvoices(res.data.payments || []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    }
  };

  const fetchWebhookLogs = async () => {
    try {
      const res = await adminAPI.getWebhookLogs({ skip: page * limit, limit });
      setWebhookLogs(res.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch webhook logs:', error);
    }
  };

  const handleRetryPayment = async (userId) => {
    setRetrying(userId);
    try {
      await adminAPI.retryPayment(userId);
      toast.success('Payment retry initiated');
      fetchPastDueAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment retry failed');
    } finally {
      setRetrying(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const COLORS = ['#1B5AFF', '#4F46E5', '#10B981', '#F59E0B'];

  const refreshActions = (
    <Button
      onClick={fetchFinancialData}
      variant="outline"
      className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF]"
    >
      <RefreshCw className="w-4 h-4 mr-2" />
      Refresh
    </Button>
  );

  if (loading) {
    return (
      <AdminLayout title="Financial Operations" breadcrumbs={[{ label: 'Financial' }]}>
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 text-[#1B5AFF] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const subscriptionDistribution = [
    { name: 'Starter', value: financialData?.subscription_counts?.starter || 0, color: '#4F46E5' },
    { name: 'Growth', value: financialData?.subscription_counts?.growth || 0, color: '#1B5AFF' },
    { name: 'Scale', value: financialData?.subscription_counts?.scale || 0, color: '#8B5CF6' },
    { name: 'Pro (Employee)', value: financialData?.subscription_counts?.pro_employees || 0, color: '#10B981' }
  ].filter(item => item.value > 0);

  return (
    <AdminLayout title="Financial Operations" breadcrumbs={[{ label: 'Financial' }]} actions={refreshActions}>
      <div data-testid="financial-operations">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#FFFFFF] border border-[#E8EDF5] mb-6 flex-wrap">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              Revenue Dashboard
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              Subscriptions
            </TabsTrigger>
            <TabsTrigger value="past-due" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg relative">
              Past Due
              {financialData?.past_due_count > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#E5484D] text-white rounded-full text-xs flex items-center justify-center">
                  {financialData.past_due_count}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="cancelled" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              Cancelled
            </TabsTrigger>
            <TabsTrigger value="invoices" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              Payments
            </TabsTrigger>
            <TabsTrigger value="webhooks" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              Webhook Logs
            </TabsTrigger>
          </TabsList>

          {/* Revenue Dashboard */}
          <TabsContent value="overview" className="space-y-6">
            {/* Top Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  <span className="text-[#5B6472] text-sm">MRR</span>
                </div>
                <p className="text-3xl font-bold text-[#0A0F1E]">{formatCurrency(financialData?.mrr || 0)}</p>
                <p className="text-green-400 text-sm mt-1">+12% vs last month</p>
              </Card>

              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                  <span className="text-[#5B6472] text-sm">ARR</span>
                </div>
                <p className="text-3xl font-bold text-[#0A0F1E]">{formatCurrency(financialData?.arr || 0)}</p>
                <p className="text-blue-400 text-sm mt-1">Annual projection</p>
              </Card>

              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-purple-400" />
                  <span className="text-[#5B6472] text-sm">ARPU</span>
                </div>
                <p className="text-3xl font-bold text-[#0A0F1E]">{formatCurrency(financialData?.arpu || 0)}</p>
                <p className="text-purple-400 text-sm mt-1">Avg revenue per user</p>
              </Card>

              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="w-5 h-5 text-red-400" />
                  <span className="text-[#5B6472] text-sm">Churn Rate</span>
                </div>
                <p className="text-3xl font-bold text-[#0A0F1E]">{financialData?.churnRate || 0}%</p>
                <p className="text-red-400 text-sm mt-1">Monthly churn</p>
              </Card>
            </div>

            {/* Revenue Charts */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* MRR Growth Chart */}
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                <h3 className="text-[#0A0F1E] font-semibold mb-4">MRR Growth</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={financialData?.mrrGrowth || []}>
                    <defs>
                      <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1B5AFF" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#1B5AFF" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8EDF5" />
                    <XAxis dataKey="month" stroke="#5B6472" />
                    <YAxis stroke="#5B6472" tickFormatter={(v) => `€${v}`} />
                    <Tooltip 
                      contentStyle={{ background: '#FFFFFF', border: '1px solid #E8EDF5', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value) => [formatCurrency(value), 'MRR']}
                    />
                    <Area type="monotone" dataKey="mrr" stroke="#1B5AFF" fill="url(#mrrGradient)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>

              {/* Subscription Distribution */}
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                <h3 className="text-[#0A0F1E] font-semibold mb-4">Subscription Distribution</h3>
                {subscriptionDistribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={subscriptionDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {subscriptionDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ background: '#FFFFFF', border: '1px solid #E8EDF5', borderRadius: '8px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-[#5B6472]">
                    <p>No subscription data available</p>
                  </div>
                )}
                
                {/* Legend */}
                <div className="flex flex-wrap justify-center gap-4 mt-4">
                  {subscriptionDistribution.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-[#5B6472] text-sm">{item.name}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            {/* Revenue Breakdown */}
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
              <h3 className="text-[#0A0F1E] font-semibold mb-4">Revenue Breakdown</h3>
              <div className="grid md:grid-cols-5 gap-4">
                <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                  <p className="text-[#5B6472] text-sm">Starter Plans</p>
                  <p className="text-2xl font-bold text-[#0A0F1E] mt-1">{financialData?.subscription_counts?.starter || 0}</p>
                  <p className="text-green-400 text-sm">{formatCurrency((financialData?.subscription_counts?.starter || 0) * 299)}/mo</p>
                </div>
                <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                  <p className="text-[#5B6472] text-sm">Growth Plans</p>
                  <p className="text-2xl font-bold text-[#0A0F1E] mt-1">{financialData?.subscription_counts?.growth || 0}</p>
                  <p className="text-green-400 text-sm">{formatCurrency((financialData?.subscription_counts?.growth || 0) * 799)}/mo</p>
                </div>
                <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                  <p className="text-[#5B6472] text-sm">Scale Plans</p>
                  <p className="text-2xl font-bold text-[#0A0F1E] mt-1">{financialData?.subscription_counts?.scale || 0}</p>
                  <p className="text-green-400 text-sm">{formatCurrency((financialData?.subscription_counts?.scale || 0) * 1999)}/mo</p>
                </div>
                <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                  <p className="text-[#5B6472] text-sm">Pro Employees</p>
                  <p className="text-2xl font-bold text-[#0A0F1E] mt-1">{financialData?.subscription_counts?.pro_employees || 0}</p>
                  <p className="text-green-400 text-sm">{formatCurrency((financialData?.subscription_counts?.pro_employees || 0) * 9.90)}/mo</p>
                </div>
                <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                  <p className="text-[#5B6472] text-sm">Active Trials</p>
                  <p className="text-2xl font-bold text-[#0A0F1E] mt-1">{financialData?.active_trials || 0}</p>
                  <p className="text-amber-400 text-sm">Pending conversion</p>
                </div>
              </div>
            </Card>

            {/* Payment Stats */}
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
              <h3 className="text-[#0A0F1E] font-semibold mb-4">Payment Health</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <p className="text-green-400 font-medium">Successful Payments</p>
                  </div>
                  <p className="text-2xl font-bold text-[#0A0F1E]">{financialData?.payment_stats?.successful || 0}</p>
                </div>
                <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="w-5 h-5 text-red-400" />
                    <p className="text-red-400 font-medium">Failed Payments</p>
                  </div>
                  <p className="text-2xl font-bold text-[#0A0F1E]">{financialData?.payment_stats?.failed || 0}</p>
                </div>
                <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-amber-400" />
                    <p className="text-amber-400 font-medium">Past Due Accounts</p>
                  </div>
                  <p className="text-2xl font-bold text-[#0A0F1E]">{financialData?.past_due_count || 0}</p>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Subscriptions Table */}
          <TabsContent value="subscriptions">
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
              {subscriptions.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#F5F8FF]/50">
                        <tr>
                          <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Company</th>
                          <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Email</th>
                          <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Plan</th>
                          <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Billing Status</th>
                          <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Stripe ID</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E8EDF5]">
                        {subscriptions.map((sub) => (
                          <tr key={sub.id} className="hover:bg-[#F5F8FF]/30">
                            <td className="p-4 text-[#0A0F1E]">{sub.company}</td>
                            <td className="p-4 text-[#5B6472]">{sub.email}</td>
                            <td className="p-4">
                              <Badge className={
                                sub.tier === 'growth' ? 'bg-purple-500/20 text-purple-400 border-0' :
                                sub.tier === 'starter' ? 'bg-blue-500/20 text-blue-400 border-0' :
                                sub.tier === 'scale' ? 'bg-pink-500/20 text-pink-400 border-0' :
                                'bg-gray-500/20 text-gray-400 border-0'
                              }>
                                {sub.tier}
                              </Badge>
                            </td>
                            <td className="p-4">
                              <Badge className={
                                sub.status === 'active' ? 'bg-green-500/20 text-green-400 border-0' :
                                sub.status === 'past_due' ? 'bg-red-500/20 text-red-400 border-0' :
                                sub.status === 'trial' ? 'bg-amber-500/20 text-amber-400 border-0' :
                                'bg-gray-500/20 text-gray-400 border-0'
                              }>
                                {sub.status || 'none'}
                              </Badge>
                            </td>
                            <td className="p-4 text-[#5B6472] text-xs font-mono">
                              {sub.stripeCustomerId || 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  <div className="flex items-center justify-between p-4 border-t border-[#E8EDF5]">
                    <p className="text-[#5B6472] text-sm">Page {page + 1}</p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        className="border-[#E8EDF5] text-[#0A0F1E]"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPage(p => p + 1)}
                        disabled={subscriptions.length < limit}
                        className="border-[#E8EDF5] text-[#0A0F1E]"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-16 text-[#5B6472]">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No subscriptions found</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Past Due Accounts */}
          <TabsContent value="past-due">
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
              {pastDueAccounts.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#F5F8FF]/50">
                        <tr>
                          <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Company</th>
                          <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Email</th>
                          <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Plan</th>
                          <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Failed Attempts</th>
                          <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Last Failed</th>
                          <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E8EDF5]">
                        {pastDueAccounts.map((account) => (
                          <tr key={account.user_id} className="hover:bg-[#F5F8FF]/30">
                            <td className="p-4 text-[#0A0F1E]">{account.company_name}</td>
                            <td className="p-4 text-[#5B6472]">{account.email}</td>
                            <td className="p-4">
                              <Badge className="bg-purple-500/20 text-purple-400 border-0">
                                {account.subscription_tier}
                              </Badge>
                            </td>
                            <td className="p-4">
                              <Badge className={
                                account.consecutive_failures >= 3 
                                  ? 'bg-red-500/20 text-red-400 border-0' 
                                  : 'bg-amber-500/20 text-amber-400 border-0'
                              }>
                                {account.consecutive_failures}x failed
                              </Badge>
                            </td>
                            <td className="p-4 text-[#5B6472] text-sm">
                              {formatDate(account.last_failed_at)}
                            </td>
                            <td className="p-4">
                              <Button
                                size="sm"
                                onClick={() => handleRetryPayment(account.user_id)}
                                disabled={retrying === account.user_id}
                                className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-lg"
                              >
                                {retrying === account.user_id ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <RotateCcw className="w-4 h-4 mr-1" />
                                    Retry
                                  </>
                                )}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-16 text-[#5B6472]">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400 opacity-50" />
                  <p className="text-lg">No Past Due Accounts</p>
                  <p className="text-sm">All accounts are in good standing</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Cancelled Accounts */}
          <TabsContent value="cancelled">
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
              {cancelledAccounts.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#F5F8FF]/50">
                      <tr>
                        <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Email</th>
                        <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Previous Plan</th>
                        <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Cancelled At</th>
                        <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Access Until</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E8EDF5]">
                      {cancelledAccounts.map((cancel) => (
                        <tr key={cancel.id} className="hover:bg-[#F5F8FF]/30">
                          <td className="p-4 text-[#0A0F1E]">{cancel.email}</td>
                          <td className="p-4">
                            <Badge className="bg-gray-500/20 text-gray-400 border-0">
                              {cancel.previous_plan || 'N/A'}
                            </Badge>
                          </td>
                          <td className="p-4 text-[#5B6472]">{formatDate(cancel.cancelled_at)}</td>
                          <td className="p-4 text-amber-400">{formatDate(cancel.access_until)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-16 text-[#5B6472]">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No Cancelled Accounts</p>
                  <p className="text-sm">No subscriptions have been cancelled</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Payment History */}
          <TabsContent value="invoices">
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
              {invoices.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#F5F8FF]/50">
                      <tr>
                        <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Email</th>
                        <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Amount</th>
                        <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Status</th>
                        <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Date</th>
                        <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Invoice ID</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E8EDF5]">
                      {invoices.map((payment) => (
                        <tr key={payment.id} className="hover:bg-[#F5F8FF]/30">
                          <td className="p-4 text-[#0A0F1E]">{payment.email}</td>
                          <td className="p-4 text-green-400 font-medium">
                            {formatCurrency(payment.amount)} {payment.currency}
                          </td>
                          <td className="p-4">
                            <Badge className={
                              payment.status === 'succeeded' 
                                ? 'bg-green-500/20 text-green-400 border-0' 
                                : 'bg-red-500/20 text-red-400 border-0'
                            }>
                              {payment.status}
                            </Badge>
                          </td>
                          <td className="p-4 text-[#5B6472]">{formatDate(payment.created_at)}</td>
                          <td className="p-4 text-[#5B6472] text-xs font-mono">{payment.invoice_id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-16 text-[#5B6472]">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No Payment Records</p>
                  <p className="text-sm">Payment records will appear here as webhooks are processed</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Webhook Logs */}
          <TabsContent value="webhooks">
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
              {webhookLogs.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#F5F8FF]/50">
                      <tr>
                        <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Event Type</th>
                        <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Status</th>
                        <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Timestamp</th>
                        <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Event ID</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E8EDF5]">
                      {webhookLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-[#F5F8FF]/30">
                          <td className="p-4">
                            <code className="text-[#1B5AFF] bg-[#1B5AFF]/10 px-2 py-1 rounded text-sm">
                              {log.event_type}
                            </code>
                          </td>
                          <td className="p-4">
                            <Badge className={
                              log.status === 'processed' ? 'bg-green-500/20 text-green-400 border-0' :
                              log.status === 'received' ? 'bg-blue-500/20 text-blue-400 border-0' :
                              log.status === 'error' ? 'bg-red-500/20 text-red-400 border-0' :
                              'bg-gray-500/20 text-gray-400 border-0'
                            }>
                              {log.status}
                            </Badge>
                          </td>
                          <td className="p-4 text-[#5B6472] text-sm">{formatDate(log.created_at)}</td>
                          <td className="p-4 text-[#5B6472] text-xs font-mono">{log.stripe_event_id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-16 text-[#5B6472]">
                  <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No Webhook Logs</p>
                  <p className="text-sm">Stripe webhook events will be logged here</p>
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default FinancialOperations;
